// WebSocket client for the Vision Service two-tier protocol.
//
// Handles:
//  - Fast path: sendFrameAnalysis() with single-in-flight backpressure.
//    If the server hasn't acked the previous seq yet, new frames are dropped
//    (newest-wins would break seq ordering; we just skip).
//  - Advanced path: sendKeyframe() with bounded in-flight by keyframe_id.
//  - Typed message dispatch to listeners.

import { Landmark } from './poseDetector';

export interface SensorData {
  power_w?: number;
  cadence_rpm?: number;
  heart_rate_bpm?: number;
  speed_mps?: number;
}

export interface FrameAnalysisPayload {
  seq: number;
  user_id: string;
  session_id: string;
  timestamp_ms: number;
  image_dims: [number, number];
  landmarks: Landmark[];
  segmentation: {
    frontal_area_px: number;
    bbox: [number, number, number, number];
    segment_pixels: {
      head: number;
      torso: number;
      arms: number;
      legs: number;
    };
  };
  calibration: {
    px_per_m: number;
    source: string;
    confidence: number;
  };
  position?: {
    class: string;
    confidence: number;
    torso_angle_deg: number;
    /** Track 1.4 — continuous Cd from class-probability blend. Server uses
     *  this when present, falls back to the hard class lookup otherwise. */
    cd_blended?: number;
    /** Track 1.4 — soft-max probabilities per class. Surfaced for
     *  diagnostics + future event-trigger logic. */
    probs?: Partial<Record<string, number>>;
  };
  sensors?: SensorData;
}

export interface KeyframePayload {
  keyframe_id: string;
  user_id: string;
  session_id: string;
  timestamp_ms: number;
  frame_jpeg_b64: string;
  landmarks: Landmark[];
  calibration: {
    rider_height_m: number;
    rider_height_px: number;
  };
  sensors?: SensorData;
  refinements_requested?: string[];
}

export interface AnalysisResult {
  type: 'analysis_result';
  seq: number;
  cda: number;
  cd: number;
  frontal_area_m2: number;
  drag_force_n: number;
  drag_power_w: number;
  segments?: {
    head_pct: number;
    torso_pct: number;
    arms_pct: number;
    legs_pct: number;
    // Track 2.1 — physics-attributed drag in watts, summing to drag_power_w.
    head_w?: number;
    torso_w?: number;
    arms_w?: number;
    legs_w?: number;
  };
  predicted_speed_mps?: number;
  predicted_speed_kph?: number;
  watts_saved?: number;
  position_class: string;
  calibration_source: string;
  calibration_confidence: number;
  tier: 'fast';
  inference_ms?: number;
}

export interface KeyframeAck {
  type: 'keyframe_ack';
  keyframe_id: string;
  queue_position: number;
  eta_ms: number;
}

export interface Refinement {
  type: 'refinement';
  keyframe_id: string;
  processing_time_ms: number;
  cda: number;
  frontal_area_m2: number;
  drag_force_n: number;
  drag_power_w: number;
  segments?: {
    head_pct: number;
    torso_pct: number;
    arms_pct: number;
    legs_pct: number;
  };
  position_class?: string;
  position_confidence?: number;
  predicted_speed_mps?: number;
  predicted_speed_kph?: number;
  watts_saved?: number;
  tier: 'advanced';
  notes?: string;
}

export type VisionMessage =
  | AnalysisResult
  | KeyframeAck
  | Refinement
  | { type: 'error'; error: string };

export interface VisionClientHandlers {
  onAnalysisResult?: (msg: AnalysisResult) => void;
  onKeyframeAck?: (msg: KeyframeAck) => void;
  onRefinement?: (msg: Refinement) => void;
  onError?: (err: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export class VisionClient {
  private ws: WebSocket | null = null;
  private handlers: VisionClientHandlers;
  private url: string;

  private lastAckedSeq = 0;
  private lastSentSeq = 0;

  private inFlightKeyframeId: string | null = null;

  constructor(url: string, handlers: VisionClientHandlers = {}) {
    this.url = url;
    this.handlers = handlers;
  }

  connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this.lastAckedSeq = 0;
      this.lastSentSeq = 0;
      this.inFlightKeyframeId = null;
      this.handlers.onOpen?.();
    };

    this.ws.onmessage = (evt) => {
      try {
        const msg: VisionMessage = JSON.parse(evt.data);
        this.handleMessage(msg);
      } catch (e) {
        console.error('Failed to parse vision message', e);
      }
    };

    this.ws.onclose = () => {
      this.handlers.onClose?.();
    };

    this.ws.onerror = (e) => {
      console.error('VisionClient WebSocket error', e);
    };
  }

  close(): void {
    this.ws?.close();
    this.ws = null;
  }

  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Send a fast-path frame analysis. Returns true if sent, false if dropped
   * because the previous frame hasn't been acked yet.
   */
  sendFrameAnalysis(payload: Omit<FrameAnalysisPayload, 'seq'>): boolean {
    if (!this.isOpen()) return false;

    // Single-in-flight backpressure: only send if the previous seq is acked.
    if (this.lastSentSeq > this.lastAckedSeq) {
      return false;
    }

    this.lastSentSeq += 1;
    const full: FrameAnalysisPayload & { type: string } = {
      type: 'frame_analysis',
      ...payload,
      seq: this.lastSentSeq,
    };
    this.ws!.send(JSON.stringify(full));
    return true;
  }

  /**
   * Send an advanced-path keyframe. Returns true if sent, false if another
   * keyframe is still in flight.
   */
  sendKeyframe(payload: KeyframePayload): boolean {
    if (!this.isOpen()) return false;
    if (this.inFlightKeyframeId) return false;

    this.inFlightKeyframeId = payload.keyframe_id;
    this.ws!.send(JSON.stringify({ type: 'keyframe', ...payload }));
    return true;
  }

  private handleMessage(msg: VisionMessage): void {
    switch (msg.type) {
      case 'analysis_result':
        if (msg.seq > this.lastAckedSeq) this.lastAckedSeq = msg.seq;
        this.handlers.onAnalysisResult?.(msg);
        break;
      case 'keyframe_ack':
        this.handlers.onKeyframeAck?.(msg);
        break;
      case 'refinement':
        if (this.inFlightKeyframeId === msg.keyframe_id) {
          this.inFlightKeyframeId = null;
        }
        this.handlers.onRefinement?.(msg);
        break;
      case 'error':
        this.handlers.onError?.(msg.error);
        break;
    }
  }
}
